import { useCallback, useEffect, useState } from 'react';
import { Link2, MessagesSquare, Plus, UserRound, UserRoundCheck, Users } from 'lucide-react';
import { rosterApi } from '../api/auth';
import type { RosterRow } from '../api/auth';
import { Button } from '../ui/Button';
import { TextField } from '../ui/Field';
import type { Me } from '../api/auth';
import { AppHeader } from '../ui/AppHeader';
import { Footer, Gutter, Page } from '../ui/Chrome';
import { CopyLink } from '../ui/CopyLink';
import { EmptyState } from '../ui/EmptyState';
import { AddAdultForm, AddYouthForm } from './RosterForms';

type Panel = 'none' | 'adult' | 'youth';

export function RosterScreen({
  unitId,
  unitName,
  me,
  onNavigate,
}: {
  unitId: string;
  unitName: string;
  me: Me;
  onNavigate: (path: string) => void;
}) {
  const [rows, setRows] = useState<RosterRow[] | null>(null);
  const [panel, setPanel] = useState<Panel>('none');
  const [error, setError] = useState<string | null>(null);
  const [links, setLinks] = useState<Record<string, { url: string; expiresAt: string }>>({});
  // Rows whose last address save withdrew the sign-up link already sent (TROOP-232). The server's
  // answer, kept per row: the copy panel that carried the explanation is the very thing the
  // withdrawal removes, so without this the link vanishes off the row and nothing says why.
  const [withdrawn, setWithdrawn] = useState<Record<string, true>>({});

  const load = useCallback(async () => {
    try {
      setRows(await rosterApi.list(unitId));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the roster.');
    }
  }, [unitId]);

  useEffect(() => {
    void load();
  }, [load]);

  // The API is the enforcement point; this only decides what renders, so a member is not shown
  // a control that would be refused.
  const canEditRoster =
    me.memberships.find((m) => m.unitId === unitId)?.permissionLevel === 'Admin';

  async function savePhone(personId: string, phone: string | null) {
    try {
      await rosterApi.setPhone(unitId, personId, phone);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That number could not be saved.');
    }
  }

  async function saveEmail(personId: string, email: string) {
    try {
      const result = await rosterApi.setEmail(unitId, personId, email);

      // A link bound to the address this replaced is revoked by the save, since TROOP-232, so a
      // copy of it on this row is a URL a leader could still hand over and which now refuses
      // whoever opens it. Dropped on the server's answer rather than on a comparison made here:
      // whether the write orphaned a link is `PersonEmail.Normalize`'s question, and a save that
      // stores the same address differently capitalised keeps the link -- hiding it then would lose
      // the copy a leader standing next to somebody at a meeting is using.
      if (result.cancelledSignUpLink) {
        setLinks((current) => {
          const { [personId]: _revoked, ...rest } = current;
          return rest;
        });
      }

      setWithdrawn((current) => {
        if (result.cancelledSignUpLink) return { ...current, [personId]: true };
        const { [personId]: _cleared, ...rest } = current;
        return rest;
      });
      // Reloaded rather than patched in place: the sign-up link button on this same row is gated
      // on whether there is an address, so a local edit that skipped the reload would leave it
      // disabled with a reason that had stopped being true.
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That address could not be saved.');
    }
  }

  async function issueLink(personId: string) {
    try {
      const link = await rosterApi.claimLink(unitId, personId);
      setLinks((current) => ({ ...current, [personId]: { url: link.url, expiresAt: link.expiresAt } }));

      // The notice is about the link this one replaces, so it must not outlive it -- otherwise the
      // row says the sign-up link no longer works directly above a copy of the one that does.
      setWithdrawn((current) => {
        const { [personId]: _replaced, ...rest } = current;
        return rest;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create a link.');
    }
  }

  const adults = rows?.filter((r) => r.classification === 'Adult' && !r.isGuardianOnly) ?? [];
  const youth = rows?.filter((r) => r.classification === 'Youth') ?? [];
  const guardians = rows?.filter((r) => r.isGuardianOnly) ?? [];

  return (
    <Page>
      <AppHeader me={me} currentUnitId={unitId} onNavigate={onNavigate} />

      <section className="rule-canonical">
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            <p className="t-eyebrow" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
              {unitName}
            </p>
            <h1
              className="t-heading"
              style={{ color: 'var(--color-text-primary)', margin: 'var(--space-2) 0 var(--space-4)' }}
            >
              Roster
            </h1>

            <div className="flex flex-wrap gap-3">
              <Button variant="primary" onClick={() => setPanel(panel === 'adult' ? 'none' : 'adult')}>
                <Plus size={16} aria-hidden="true" />
                Add an adult
              </Button>
              <Button variant="secondary" onClick={() => setPanel(panel === 'youth' ? 'none' : 'youth')}>
                <Plus size={16} aria-hidden="true" />
                Add a youth
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  window.location.href = `/units/${unitId}/conversations/new`;
                }}
              >
                <MessagesSquare size={16} aria-hidden="true" />
                Start a conversation
              </Button>
            </div>
          </div>
        </Gutter>
      </section>

      <section className="flex-1" style={{ background: 'var(--color-surface-sunken)' }}>
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            {error && (
              <p
                className="t-body"
                role="alert"
                style={{
                  background: 'var(--color-status-blocked-surface)',
                  border: '1px solid var(--color-status-blocked)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-3)',
                  marginBottom: 'var(--space-4)',
                  color: 'var(--color-text-primary)',
                }}
              >
                {error}
              </p>
            )}

            {panel === 'adult' && (
              <PanelCard>
                <AddAdultForm
                  unitId={unitId}
                  onDone={() => {
                    setPanel('none');
                    void load();
                  }}
                />
              </PanelCard>
            )}

            {panel === 'youth' && (
              <PanelCard>
                <AddYouthForm
                  unitId={unitId}
                  onDone={() => {
                    setPanel('none');
                    void load();
                  }}
                />
              </PanelCard>
            )}

            {rows?.length === 0 && (
              <EmptyState
                icon={Users}
                headline="Nobody here yet"
                explanation="Add the adults first, then the youth — each one needs a parent or guardian recorded at the same time."
                action={{ label: 'Add an adult', onClick: () => setPanel('adult') }}
              />
            )}

            {adults.length > 0 && (
              <Group title="Adults" count={adults.length}>
                {adults.map((row) => (
                  <MemberRow
                    key={row.personId}
                    row={row}
                    link={links[row.personId]}
                    withdrewLink={withdrawn[row.personId]}
                    onIssueLink={() => void issueLink(row.personId)}
                    onSetPhone={
                      canEditRoster
                        ? (phone) => void savePhone(row.personId, phone)
                        : undefined
                    }
                    onSetEmail={
                      canEditRoster
                        ? (email) => void saveEmail(row.personId, email)
                        : undefined
                    }
                  />
                ))}
              </Group>
            )}

            {youth.length > 0 && (
              <Group title="Youth" count={youth.length}>
                {youth.map((row) => (
                  <MemberRow
                    key={row.personId}
                    row={row}
                    link={links[row.personId]}
                    withdrewLink={withdrawn[row.personId]}
                    onIssueLink={() => void issueLink(row.personId)}
                  />
                ))}
              </Group>
            )}

            {guardians.length > 0 && (
              <Group
                title="Parents and guardians"
                count={guardians.length}
                // Says what listing them does and does not mean. A parent reads
                // their own child's threads; being here is not unit membership.
                note="Not unit members. They see the conversations their own child is in, and each one needs an account before that child can sign in."
              >
                {guardians.map((row) => (
                  <MemberRow
                    key={row.personId}
                    row={row}
                    link={links[row.personId]}
                    withdrewLink={withdrawn[row.personId]}
                    onIssueLink={() => void issueLink(row.personId)}
                    // Guardians get one too, and they are the point: a young person's row carries
                    // no number, so their parent's is the one a leader will actually ring.
                    onSetPhone={
                      canEditRoster
                        ? (phone) => void savePhone(row.personId, phone)
                        : undefined
                    }
                    // And an address, for the same reason the adults above get one: a guardian
                    // entered without one cannot be sent a sign-up link, and a young person cannot
                    // sign in until a guardian has (rule 11) -- so the child is stuck behind it.
                    onSetEmail={
                      canEditRoster
                        ? (email) => void saveEmail(row.personId, email)
                        : undefined
                    }
                  />
                ))}
              </Group>
            )}
          </div>
        </Gutter>
      </section>

      <Footer />
    </Page>
  );
}

function PanelCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--elevation-raised)',
        padding: 'var(--space-5)',
        marginBottom: 'var(--space-5)',
      }}
    >
      {children}
    </div>
  );
}

function Group({
  title,
  count,
  note,
  children,
}: {
  title: string;
  count: number;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <section style={{ marginBottom: 'var(--space-6)' }}>
      <h2
        className="t-label"
        style={{ color: 'var(--color-text-muted)', margin: '0 0 var(--space-2)' }}
      >
        {title} &middot; <span className="tabular">{count}</span>
      </h2>
      {note && (
        <p
          className="t-caption"
          style={{
            color: 'var(--color-text-secondary)',
            margin: '0 0 var(--space-3)',
            maxWidth: '46ch',
          }}
        >
          {note}
        </p>
      )}
      <div className="flex flex-col" style={{ gap: 'var(--space-3)' }}>
        {children}
      </div>
    </section>
  );
}

function MemberRow({
  row,
  link,
  withdrewLink,
  onIssueLink,
  onSetPhone,
  onSetEmail,
}: {
  row: RosterRow;
  link?: { url: string; expiresAt: string };
  /** The last address save withdrew the link already sent (TROOP-232). The server said so. */
  withdrewLink?: boolean;
  onIssueLink: () => void;
  /** Undefined for a caller who may not change the roster — no control, rather than a dead one. */
  onSetPhone?: (phone: string | null) => void;
  /** Same, and adults only: see the control's own comment for why that is rule 10 and not tidiness. */
  onSetEmail?: (email: string) => void;
}) {
  const isAdult = row.classification === 'Adult';

  const caption = [
    row.positionTitle,

    // "None" is a real value for a guardian, who holds no membership. Printing it
    // would read as a permission level they were denied.
    row.permissionLevel !== 'Member' && row.permissionLevel !== 'None' ? row.permissionLevel : '',

    row.email,
    row.phone,

    // A young person's address is withheld from everyone but their own guardians, so a
    // blank here is the rule working rather than missing data. Saying where to go instead
    // stops a leader hunting for it. The same sentence covers the phone number, which the
    // product does not hold for them at all (TROOP-132).
    !row.email && !isAdult ? 'Contact their parent' : '',
  ].filter(Boolean).join(' · ');

  return (
    <div
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--elevation-raised)',
        padding: 'var(--space-4)',
      }}
    >
      <div className="flex flex-wrap items-start justify-between" style={{ gap: 'var(--space-3)' }}>
        <div className="min-w-0">
          <div className="flex items-center" style={{ gap: 'var(--space-2)' }}>
            {/* Shape carries the classification, as it does everywhere else: a
                circle for adults, a rounded square for youth. Never colour. */}
            <span
              aria-hidden="true"
              className="inline-flex items-center justify-center shrink-0"
              style={{
                width: 26,
                height: 26,
                borderRadius: isAdult ? '50%' : 'var(--radius-sm)',
                border: isAdult
                  ? '1.5px solid var(--color-brand-ink)'
                  : '1.5px dashed var(--color-border-strong)',
                color: 'var(--color-text-secondary)',
              }}
            >
              {isAdult ? <UserRoundCheck size={13} /> : <UserRound size={13} />}
            </span>

            <span className="t-body-strong" style={{ color: 'var(--color-text-primary)' }}>
              {row.displayName}
            </span>
            <span className="sr-only">{isAdult ? '(adult)' : '(youth)'}</span>
          </div>

          {/* Joined once rather than each fragment carrying its own separator. A position title
              may be empty since TROOP-235 — arriving from the API, not from this app's own form,
              which still requires one — and a fragment that prefixes its own " · " opens the
              caption with a dangling separator for exactly the people who have no title. This is
              the screen made correct ahead of the form rather than a break anybody has seen.
              Empty throughout means no line at all. */}
          {caption && (
            <p
              className="t-caption"
              style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-1) 0 0' }}
            >
              {caption}
            </p>
          )}

          {/* Adults only, and absent rather than disabled for a young person: there is no
              control to explain, because the person a leader should contact is the parent named
              below. TROOP-132. */}
          {isAdult && onSetPhone && (
            <PhoneControl phone={row.phone} onSave={onSetPhone} />
          )}

          {/* Adults only, for rule 10's reason rather than for convenience: a young person's
              address is their guardian's to set from /family, and a leader who could type it here
              would have it. Absent rather than disabled for a youth, exactly like the number.

              It exists at all because TROOP-224 removed what used to stand in for it: the claim
              link wrote the address on redemption, so "you can add it later" was true without a
              control. It now needs one to bind to. */}
          {isAdult && onSetEmail && (
            <EmailControl email={row.email} onSave={onSetEmail} />
          )}

          {row.guardianNames.length > 0 && (
            <p
              className="t-caption"
              style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-1) 0 0' }}
            >
              {/* The same field, read from both ends: a youth's parents, or the
                  children who are the reason a parent is on this screen. */}
              {row.isGuardianOnly ? 'Parent of' : 'Guardians'}: {row.guardianNames.join(', ')}
            </p>
          )}
        </div>

        <div className="shrink-0">
          {row.hasAccount ? (
            <span className="t-caption" style={{ color: 'var(--color-status-compliant)' }}>
              Account linked
            </span>
          ) : row.canClaimAccount ? (
            <Button
              variant="secondary"
              onClick={onIssueLink}
              // No address, no link: the route refuses with a 409 since TROOP-224, and a control
              // whose only outcome is a refusal is worse than one that says why. The address
              // control sits on this same row, which is what the sentence points at --
              // `disabledReason` rather than `disabled` for exactly that reason.
              disabledReason={
                row.email
                  ? undefined
                  : `Add ${row.displayName.split(' ')[0]}'s email address first — the link is sent there, and only that address can use it.`
              }
            >
              <Link2 size={16} aria-hidden="true" />
              {/* "Send a new link", never "Resend" (TROOP-223). Issuing revokes every
                  outstanding link, so a second one necessarily kills the mail already in their
                  inbox — a Resend label promises a duplicate while delivering a replacement,
                  which is a control drawn as the state it records. Before there is one, there is
                  nothing to replace and the label says what it does. */}
              {link ? 'Send a new link' : 'Email a sign-up link'}
            </Button>
          ) : (
            // Not a disabled button. A leader is not waiting on a permission here
            // and never gets this one — a young person's account is set up by
            // their parent, which is also the gate on youth access. A control that
            // might light up later would misdescribe that.
            <span
              className="t-caption"
              style={{ color: 'var(--color-text-secondary)', maxWidth: '14rem', display: 'block' }}
            >
              Their parent sets this up
            </span>
          )}
        </div>
      </div>

      {/* Said out loud, because the withdrawal removes the one panel that explained it
          (TROOP-232). A leader corrects a typo, the copy-link disappears, and without this the
          row gives no reason to send another -- while the address now on file is one nobody has
          been sent a link at. On the server's answer, so it is never shown for a save that kept
          the link alive. */}
      {withdrewLink && !link && (
        <p
          className="t-caption"
          style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-3) 0 0' }}
        >
          The sign-up link already sent to {row.displayName} no longer works &mdash; it was bound to
          the old address. Send a new one when you are ready.
        </p>
      )}

      {link && (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <CopyLink url={link.url} label={`A copy of what ${row.displayName} was sent`} />
          <p
            className="t-caption"
            style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-2) 0 0' }}
          >
            {/* Rewritten twice over. It said "whoever opens this links their own Google account
                to their record", which stopped being true at TROOP-224 -- the link is bound to
                their address, so nobody else's account can be linked by it -- and it is no longer
                the leader's job to relay it either, since TROOP-223 emails it. Copy-link stays for
                the leader standing next to them at a meeting. */}
            Emailed to {row.displayName}. It only works from their own address, it works once, and
            sending a new one cancels this &mdash; so does changing the address it was sent to,
            since the link is bound to it.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * A leader typing in a contact number they collected at a meeting — TROOP-132.
 *
 * Inline and always-editable rather than behind a modal, because the ordinary use is working down
 * a roster entering fifteen of them. Blank saves as "no number", so correcting a typo needs no
 * second control.
 *
 * **Never rendered for a young person.** The product holds no number for one, and the parent's row
 * is where a leader is meant to look — see `Person.PhoneNumber`, which is rule 10's reasoning
 * applied to a channel more immediate than email.
 */
function PhoneControl({
  phone,
  onSave,
}: {
  phone: string | null;
  onSave: (phone: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(phone ?? '');

  if (!open) {
    return (
      <p style={{ margin: 'var(--space-1) 0 0' }}>
        <button
          type="button"
          className="link-button t-caption"
          onClick={() => {
            setValue(phone ?? '');
            setOpen(true);
          }}
        >
          {phone ? 'Change their number' : 'Add a phone number'}
        </button>
      </p>
    );
  }

  return (
    <form
      className="flex flex-wrap items-end"
      style={{ gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}
      onSubmit={(e) => {
        e.preventDefault();
        // Trimmed to null here as well as on the server: an empty string and "no number" are the
        // same fact, and the row should not have to know which one it was sent.
        onSave(value.trim() || null);
        setOpen(false);
      }}
    >
      <div style={{ flex: '1 1 12rem' }}>
        <TextField
          label="Phone number"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="555 0134"
          maxLength={40}
          hint="However it is useful to whoever reads the printed roster."
        />
      </div>
      <Button variant="primary" type="submit">
        Save
      </Button>
      <Button variant="secondary" onClick={() => setOpen(false)}>
        Cancel
      </Button>
    </form>
  );
}

/**
 * An adult's address, edited in place beside their number (TROOP-224).
 *
 * **It has no "remove" and the phone control does**, which is the difference worth keeping: a
 * number is contact detail, and an address is what a sign-in resolves to. Clearing one would lock
 * that adult out of their own account, which is a revocation rather than a roster edit and is not
 * what this control is for. The server refuses a blank for the same reason.
 *
 * Rendered for adults only. A young person's address belongs to their guardian and is set from
 * `/family` — rule 10 is about a leader never having it, which a box here would hand them.
 */
function EmailControl({
  email,
  onSave,
}: {
  email: string | null;
  onSave: (email: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(email ?? '');

  if (!open) {
    return (
      <p style={{ margin: 'var(--space-1) 0 0' }}>
        <button
          type="button"
          className="link-button t-caption"
          onClick={() => {
            setValue(email ?? '');
            setOpen(true);
          }}
        >
          {email ? 'Change their email address' : 'Add an email address'}
        </button>
      </p>
    );
  }

  return (
    <form
      className="flex flex-wrap items-end"
      style={{ gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}
      onSubmit={(e) => {
        e.preventDefault();
        const trimmed = value.trim();
        if (!trimmed) return;
        onSave(trimmed);
        setOpen(false);
      }}
    >
      <div style={{ flex: '1 1 14rem' }}>
        <TextField
          label="Email address"
          type="email"
          required
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="them@example.com"
          maxLength={320}
          // The second sentence is TROOP-232's, and it is the standing rule rather than a report:
          // a link is bound to the address it was sent to, so moving the address withdraws it.
          // `FamilyScreen`'s hint carries the same sentence for a parent.
          hint="How they sign in, and where their sign-up link is sent. Changing it cancels a link already sent to the old address."
        />
      </div>
      <Button variant="primary" type="submit">
        Save
      </Button>
      <Button variant="secondary" onClick={() => setOpen(false)}>
        Cancel
      </Button>
    </form>
  );
}
